import {
  AGENT_UPLOAD_REFUSAL_MESSAGES,
  DataConnectionCredentials,
  isAgentUploadRefusalReason,
  SocketEvents,
  type UploadPhase,
  type UploadTarget,
} from "common";
import { CHUNK_SIZE, MAX_UPLOAD_FILE_SIZE } from "@via/utils/constants.tsx";
import { bytesToGB } from "@via/utils/helpers.tsx";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import { Dispatch, SetStateAction } from "react";
import { resetSocket } from "@via/utils/socket";

// Bound on the setup handshake, from opening the socket to SetupSuccess or SetupError. The
// slowest setup is SSH: ssh2 gives a connection 20 s to become ready and ssh2-sftp-client
// retries once after a 25 s backoff, so an unreachable host already answers with SetupError in
// about 65 s. 90 s stays clear of that and only fires when nothing answers at all.
export const UPLOAD_SETUP_TIMEOUT_MS = 90_000;

export type UploadProgressEntry = {
  filename: string;
  progress: number;
  /** Agent uploads only (agent-5-5): browser to server, then server to cluster. */
  phase?: UploadPhase;
  /**
   * #1024: why the server refused this file (the same text as the toast). Set by UploadError so
   * the row stops reading as an upload stuck at 0%, and cleared when the next batch starts.
   */
  error?: string;
};

/** The readable sentence for a typed agent refusal, or the server's own message for anything else. */
export const uploadRefusalDetails = (
  payload: { message?: string; reason?: unknown } | undefined
): string =>
  isAgentUploadRefusalReason(payload?.reason)
    ? AGENT_UPLOAD_REFUSAL_MESSAGES[payload.reason]
    : (payload?.message ?? "");

// agent-5-5: the backend admits at most this many files in flight per user on an agent upload
// (AGENT_UPLOAD_MAX_IN_FLIGHT_PER_USER default). With a target set the client queues the rest
// instead of starting them all and failing the batch. SSH uploads keep their own concurrency.
export const AGENT_UPLOAD_MAX_IN_FLIGHT = 3;

export const fileUpload = (
  fileUploadPath: string,
  files: File[],
  connectionCredentials: DataConnectionCredentials,
  setNotification: (_notification?: any) => null,
  // Called once the batch finished: the last placed path, and each file's own placed path by
  // normalized file name, for callers that upload several files.
  handleFileUploadSuccess: (
    placedPath?: string,
    placedPaths?: Record<string, string>
  ) => void,
  setIsLoading: (loading: boolean) => void,
  setProgressPercentage: Dispatch<SetStateAction<UploadProgressEntry[]>>,
  target?: UploadTarget
) => {
  // Track which files have finished uploading.
  // Use a Set so that if the backend emits multiple "UploadSuccess"
  // events for the same filename (e.g. per chunk), we only count
  // that file once towards completion.
  const finishedFiles = new Set<string>();
  let placedPath: string | undefined;
  const placedPaths: Record<string, string> = {};

  // Concurrent upload queue system
  const MAX_CONCURRENT_UPLOADS = target ? AGENT_UPLOAD_MAX_IN_FLIGHT : 3;
  const fileQueue: string[] = []; // Files waiting to be uploaded
  const activeUploads = new Set<string>(); // Currently uploading files (max 3)

  // Function to start next file from queue (will be defined after sendNextChunk)
  let startNextFile: (() => void) | null = null;

  if (files.some(file => file.size === 0)) {
    setNotification({
      id: "file-upload-no-content",
      message: "Setup failed. Unable to start file upload.",
      details: "File has no content",
      variant: NOTIFICATION.ERROR,
    });
    return;
  }

  if (files.some(file => file.size >= MAX_UPLOAD_FILE_SIZE)) {
    setNotification({
      id: "file-upload-limit-exceeded",
      message: "Setup failed. Unable to start file upload.",
      details: `File size exceeds the maximum limit. (${bytesToGB(MAX_UPLOAD_FILE_SIZE)}GB)`,
      variant: NOTIFICATION.ERROR,
    });
    return;
  }

  setIsLoading(true);
  // #1024: a retry starts clean, so a row refused by the previous batch no longer shows its reason.
  setProgressPercentage(prev =>
    prev.some(entry => entry.error)
      ? prev.map(({ error: _error, ...entry }) => entry)
      : prev
  );

  const socket = resetSocket(fileUploadPath);

  // Create a promise that resolves when the setup is complete
  const setupPromise = new Promise<void>((resolve, reject) => {
    socket.on(SocketEvents.SetupSuccess, () => {
      clearTimeout(setupTimer);
      resolve();
    });

    socket.on(SocketEvents.SetupError, error => {
      clearTimeout(setupTimer);
      reject(new Error(uploadRefusalDetails(error)));
    });
  });

  /**
   * Keep chunkIndex for each file to determine which chunk should be sent next
   */
  const fileMap = new Map<
    string,
    {
      chunkIndex: number;
      totalChunks: number;
    }
  >();

  // Track upload state to prevent race conditions
  let isUploadInProgress = false;
  let isSetupComplete = false;
  let isCancelled = false;
  // Set once the connection is given up on, so a late connect cannot start the upload.
  let isAbandoned = false;

  // Normalize filename by replacing spaces with underscores and trimming whitespace
  const normalizeFilename = (filename: string): string => {
    return filename.trim().replace(/ /g, "_");
  };

  // Validate fileMap state
  const validateFileMap = (filename: string): boolean => {
    const normalizedFilename = normalizeFilename(filename);
    return fileMap.has(normalizedFilename) && isSetupComplete;
  };

  // Reset upload state and clean up socket listeners
  const resetUploadState = (closeSocket = false) => {
    clearTimeout(setupTimer);
    isUploadInProgress = false;
    isSetupComplete = false;
    isCancelled = false;

    // Clean up all event listeners
    socket.off(SocketEvents.SetupSuccess);
    socket.off(SocketEvents.SetupError);
    socket.off(SocketEvents.UploadProgress);
    socket.off(SocketEvents.UploadSuccess);
    socket.off(SocketEvents.UploadError);
    socket.off(SocketEvents.UploadCancelled);
    socket.off(SocketEvents.Disconnect);
    socket.off(SocketEvents.Connect);
    socket.off("connect_error");

    // Optionally close the socket
    if (closeSocket) {
      socket.close();
    }
  };

  // A connection that fails, or a setup that never answers, is reported like a server sent
  // UploadError and clears loading the same way, so the caller leaves its loading state.
  const abandonUpload = (details: string) => {
    if (isAbandoned) {
      return;
    }
    isAbandoned = true;

    setNotification({
      id: "file-upload-failed",
      message: "File upload failed",
      details,
      variant: NOTIFICATION.ERROR,
    });

    resetUploadState(true);

    setIsLoading(false);
  };

  // Read by the listeners above, which only run after this line.
  const setupTimer = setTimeout(
    () =>
      abandonUpload(
        `The upload service did not answer within ${UPLOAD_SETUP_TIMEOUT_MS / 1000} seconds. Please try again.`
      ),
    UPLOAD_SETUP_TIMEOUT_MS
  );

  // Track cancelled files
  const cancelledFiles = new Set<string>();

  // Cancellation function for all files
  const cancelAllUploads = () => {
    if (isCancelled) {
      return; // Already cancelled
    }

    isCancelled = true;
    isUploadInProgress = false;

    // Emit cancel event to server (no filename = cancel all)
    if (socket && socket.connected) {
      socket.emit(SocketEvents.CancelUpload);
    }

    // Clean up and reset state
    resetUploadState(true);
    setIsLoading(false);
    setProgressPercentage([]);

    setNotification({
      id: "file-upload-cancelled",
      message: "Upload cancelled",
      details:
        "File upload has been cancelled and any partial data has been cleared.",
    });
  };

  socket.on(SocketEvents.Connect, () => {
    if (isAbandoned) {
      return;
    }
    socket.emit(SocketEvents.SetupConnection, {
      ...connectionCredentials,
      ...(target ? { target } : {}),
      files: files.map(file => {
        const normalizedName = normalizeFilename(file.name);
        fileMap.set(normalizedName, {
          chunkIndex: 0,
          totalChunks: Math.ceil(file.size / CHUNK_SIZE),
        });
        return {
          filename: normalizedName,
          totalChunks: Math.ceil(file.size / CHUNK_SIZE),
          size: file.size,
        };
      }),
    });

    // Wait for the setup to complete before starting the upload
    setupPromise
      .then(() => {
        console.log("Setup is completed, file upload is starting");
        isSetupComplete = true;
        isUploadInProgress = true;

        // Initialize all files with 0% progress for UI visibility
        const normalizedFilenames = files.map(file =>
          normalizeFilename(file.name)
        );
        setProgressPercentage(
          normalizedFilenames.map(filename => ({
            filename,
            progress: 0,
          }))
        );

        // Initialize file queue with all files
        fileQueue.length = 0;
        fileQueue.push(...normalizedFilenames);
        activeUploads.clear();

        const sendNextChunk = (filename: string) => {
          try {
            const normalizedFilename = normalizeFilename(filename);

            // Only process chunks for files in activeUploads (concurrent limit enforcement)
            if (!activeUploads.has(normalizedFilename)) {
              console.log(
                `Skipping chunk for ${normalizedFilename} - not in active uploads (queue position: ${fileQueue.indexOf(normalizedFilename) + 1})`
              );
              return;
            }

            // Prevent processing if upload is cancelled globally or for this specific file
            if (isCancelled || cancelledFiles.has(normalizedFilename)) {
              console.log(`Upload cancelled, skipping chunk for ${filename}`);
              activeUploads.delete(normalizedFilename);
              return;
            }

            // Prevent processing if upload is not in progress or setup not complete
            // BUT: Don't stop if this is just one file being cancelled - other files should continue
            if (!isUploadInProgress || !isSetupComplete) {
              // Only warn if this file is not cancelled (cancelled files are expected to fail validation)
              if (!cancelledFiles.has(normalizedFilename)) {
                console.warn(`Upload not ready for filename: ${filename}`);
                setNotification({
                  id: "file-upload-not-ready",
                  message: "Upload not ready",
                  details: `Cannot process file ${filename} - upload not properly initialized`,
                  variant: NOTIFICATION.ERROR,
                });
              }
              return;
            }

            // Validate fileMap state before proceeding (skip if file is cancelled)
            if (cancelledFiles.has(normalizedFilename)) {
              return; // Already handled above, but double-check
            }

            if (!validateFileMap(normalizedFilename)) {
              console.error(
                `File data not found or setup incomplete for filename: ${normalizedFilename}`
              );

              setNotification({
                id: "file-upload-filename-mismatch",
                message: "File upload error",
                details: `File ${normalizedFilename} not found in upload queue. This may be due to a connection issue or file processing error.`,
                variant: NOTIFICATION.ERROR,
              });
              return;
            }

            const file = files.find(
              file => normalizeFilename(file.name) === normalizedFilename
            );
            const fileData = fileMap.get(normalizedFilename);

            if (!file || !fileData) {
              console.error(
                `File or file data not found for filename: ${normalizedFilename}`
              );
              setNotification({
                id: "file-upload-data-missing",
                message: "File upload error",
                details: `File data missing for ${normalizedFilename}. Please try uploading again.`,
                variant: NOTIFICATION.ERROR,
              });
              return;
            }

            const { chunkIndex, totalChunks } = fileData;
            if (chunkIndex >= totalChunks) {
              socket.emit(SocketEvents.FinishUpload, normalizedFilename); // Notify server of completion
              return;
            }

            const start = chunkIndex * CHUNK_SIZE;
            const end = Math.min(file.size, start + CHUNK_SIZE);
            const chunk = file.slice(start, end);

            // Read the chunk and send it via WebSocket
            const reader = new FileReader();
            reader.onload = e => {
              if (e.target?.result) {
                // Send the chunk
                socket.emit(SocketEvents.FileChunk, {
                  filename: normalizedFilename,
                  chunk: e.target.result,
                });
                fileMap.set(normalizedFilename, {
                  chunkIndex: chunkIndex + 1,
                  totalChunks,
                });
              }
            };
            reader.onerror = () => {
              console.error(
                `Error reading file chunk for ${normalizedFilename}`
              );
              setNotification({
                id: "file-upload-read-error",
                message: "File read error",
                details: `Failed to read file chunk for ${normalizedFilename}. Please try again.`,
                variant: NOTIFICATION.ERROR,
              });
            };
            reader.readAsArrayBuffer(chunk);
          } catch (error) {
            console.error(`Error in sendNextChunk for ${filename}:`, error);
            const errorMessage =
              error instanceof Error ? error.message : "Unknown error";
            setNotification({
              id: "file-upload-chunk-error",
              message: "Upload chunk error",
              details: `Failed to process file chunk for ${filename}. ${errorMessage}`,
              variant: NOTIFICATION.ERROR,
            });
          }
        };

        // Define function to start next file from queue
        // This adds the file to activeUploads and triggers the first chunk if the server is ready
        startNextFile = () => {
          if (
            fileQueue.length > 0 &&
            activeUploads.size < MAX_CONCURRENT_UPLOADS &&
            !isCancelled
          ) {
            const nextFilename = fileQueue.shift();
            if (nextFilename && !cancelledFiles.has(nextFilename)) {
              activeUploads.add(nextFilename);
              console.log(
                `Added file to active uploads: ${nextFilename} (${activeUploads.size}/${MAX_CONCURRENT_UPLOADS} active)`
              );
              // Check if server has already initialized this file (progress update was sent)
              // If fileMap has the file and chunkIndex is 0, server is ready - trigger first chunk
              // Otherwise, wait for server's progress update
              if (fileMap.has(nextFilename)) {
                const fileData = fileMap.get(nextFilename);
                if (fileData && fileData.chunkIndex === 0) {
                  // Server already initialized this file, trigger first chunk
                  console.log(
                    `Server ready for ${nextFilename}, sending first chunk`
                  );
                  sendNextChunk(nextFilename);
                } else {
                  // Wait for server progress update
                  console.log(
                    `Waiting for server progress update for ${nextFilename}`
                  );
                }
              } else {
                // File not in fileMap yet, wait for server progress update
                console.log(
                  `File ${nextFilename} not yet initialized, waiting for server progress update`
                );
              }
            }
          }
        };

        // Add first batch of files to activeUploads (up to MAX_CONCURRENT_UPLOADS)
        // These will start uploading when server sends progress updates after StartUpload
        for (
          let i = 0;
          i < Math.min(MAX_CONCURRENT_UPLOADS, normalizedFilenames.length);
          i++
        ) {
          const nextFilename = fileQueue.shift();
          if (nextFilename) {
            activeUploads.add(nextFilename);
            console.log(
              `Added file to initial active uploads: ${nextFilename} (${activeUploads.size}/${MAX_CONCURRENT_UPLOADS} active)`
            );
          }
        }

        socket.emit(SocketEvents.StartUpload);
        // Server will now initialize all files and send progress updates
        // Progress handler will process chunks only for files in activeUploads

        // Wait for the server to signal that the next chunk can be sent
        socket.on(
          SocketEvents.UploadProgress,
          (data: {
            filename: string;
            progress: number;
            phase?: UploadPhase;
          }) => {
            try {
              const normalizedFilename = normalizeFilename(data.filename);

              // Don't process if cancelled globally or for this specific file
              if (isCancelled || cancelledFiles.has(normalizedFilename)) {
                console.log(
                  `Skipping progress update for cancelled file: ${normalizedFilename}`
                );
                return;
              }

              // Ensure the file is still in fileMap (hasn't been removed)
              // Only skip if the file was cancelled AND removed from fileMap (after server confirmation)
              // If file is cancelled but still in fileMap (before server confirmation), we already returned above
              if (!fileMap.has(normalizedFilename)) {
                // File was removed from fileMap (likely after server confirmed cancellation)
                // But double-check it's actually cancelled - if not, this is an error
                if (!cancelledFiles.has(normalizedFilename)) {
                  console.warn(
                    `Progress update for file not in fileMap and not cancelled: ${normalizedFilename} - this shouldn't happen`
                  );
                } else {
                  console.log(
                    `Skipping progress update for cancelled file removed from fileMap: ${normalizedFilename}`
                  );
                }
                return;
              }

              // Only process progress updates for files in activeUploads
              // This ensures we maintain the concurrent limit of 3
              if (!activeUploads.has(normalizedFilename)) {
                // File is queued but not yet active - ignore progress updates until it becomes active
                return;
              }

              // agent-5-5: the server is sending this file to the cluster. Every chunk is already
              // sent and FinishUpload already went out, so this event must not drive the chunk
              // loop: sendNextChunk would emit a second FinishUpload.
              if (data.phase === "sending_to_cluster") {
                setProgressPercentage(prev =>
                  prev.map(p =>
                    p.filename === normalizedFilename
                      ? { ...p, progress: data.progress, phase: data.phase }
                      : p
                  )
                );
                return;
              }

              // Send the next chunk when the server is ready
              console.log(
                `Processing progress update for ${normalizedFilename}, progress: ${data.progress}%`
              );
              sendNextChunk(normalizedFilename);

              // Update progress percentage for this file
              setProgressPercentage(prev => {
                // Check if file exists in progress list
                const exists = prev.some(
                  p => p.filename === normalizedFilename
                );
                if (exists) {
                  // Update existing entry
                  return prev.map(p =>
                    p.filename === normalizedFilename
                      ? {
                          ...p,
                          progress: data.progress,
                          ...(data.phase ? { phase: data.phase } : {}),
                        }
                      : p
                  );
                } else {
                  // File not in list - this can happen if it was removed when cancelled
                  // But if we're here, it means the file is NOT cancelled (we checked above)
                  // So this shouldn't happen, but add it back to be safe
                  console.warn(
                    `Progress update for file not in progress list (but not cancelled): ${normalizedFilename}, adding it back`
                  );
                  return [
                    ...prev,
                    { filename: normalizedFilename, progress: data.progress },
                  ];
                }
              });
            } catch (error) {
              console.error(
                `Error handling upload progress for ${data.filename}:`,
                error
              );
              const errorMessage =
                error instanceof Error ? error.message : "Unknown error";
              setNotification({
                id: "file-upload-progress-error",
                message: "Upload progress error",
                details: `Failed to handle upload progress for ${data.filename}. ${errorMessage}`,
                variant: NOTIFICATION.ERROR,
              });
            }
          }
        );
      })
      .catch(error => {
        console.error("Setup failed:", error);
        setNotification({
          id: "file-upload-setup-failed",
          message: "Setup failed. Unable to start file upload.",
          details: error.message,
          variant: NOTIFICATION.ERROR,
        });
        setIsLoading(false);
      });
  });

  socket.on(SocketEvents.UploadSuccess, data => {
    const finishedFilename = data?.filename as string | undefined;

    if (!finishedFilename) {
      return;
    }

    const normalizedFilename = normalizeFilename(finishedFilename);

    // Don't process if cancelled globally or for this specific file
    if (isCancelled || cancelledFiles.has(normalizedFilename)) {
      return;
    }

    // If we've already marked this file as finished, ignore duplicate
    // success events to avoid spamming notifications and counters.
    if (finishedFiles.has(normalizedFilename)) {
      return;
    }

    finishedFiles.add(normalizedFilename);
    if (typeof data?.placedPath === "string") {
      placedPath = data.placedPath;
      placedPaths[normalizedFilename] = data.placedPath;
    }

    // Remove from active uploads and start next file from queue
    activeUploads.delete(normalizedFilename);
    console.log(
      `File ${normalizedFilename} completed. Active uploads: ${activeUploads.size}/${MAX_CONCURRENT_UPLOADS}`
    );

    // Start next file from queue if available
    if (startNextFile) {
      startNextFile();
    }

    // Notify once per successfully uploaded file.
    setNotification({
      id: "file-upload-success",
      message: "File uploaded successfully",
      details: finishedFilename,
      variant: NOTIFICATION.SUCCESS,
    });

    // When all distinct files in this upload batch have finished,
    // invoke the success handler and finalize the upload.
    // Only count non-cancelled files
    const nonCancelledFiles = files.filter(
      f => !cancelledFiles.has(normalizeFilename(f.name))
    );
    if (finishedFiles.size === nonCancelledFiles.length) {
      handleFileUploadSuccess(placedPath, placedPaths);
      finishUpload();
    }
  });

  socket.on(SocketEvents.UploadCancelled, () => {
    // Server confirmed cancellation (all files)
    finishUpload();
  });

  socket.on(SocketEvents.UploadError, error => {
    const refused = isAgentUploadRefusalReason(error?.reason);
    const message = refused
      ? "File upload failed"
      : error?.message || "File upload failed";
    const details = refused
      ? uploadRefusalDetails(error)
      : error?.error?.message || "";
    setNotification({
      id: "file-upload-failed",
      message,
      details,
      variant: NOTIFICATION.ERROR,
    });

    // #1024: show the refusal on the refused file's own row, so it outlasts the toast. An error
    // that names no file belongs to the batch's only file when there is just one; otherwise no
    // row is marked, because marking a sibling would blame a file the server did not refuse.
    const refusedFilename =
      typeof error?.filename === "string" && error.filename
        ? normalizeFilename(error.filename)
        : files.length === 1
          ? normalizeFilename(files[0].name)
          : undefined;
    if (refusedFilename) {
      const rowError = details || message;
      setProgressPercentage(prev =>
        prev.some(entry => entry.filename === refusedFilename)
          ? prev.map(entry =>
              entry.filename === refusedFilename
                ? { ...entry, error: rowError }
                : entry
            )
          : [
              ...prev,
              { filename: refusedFilename, progress: 0, error: rowError },
            ]
      );
    }

    if (target) {
      // agent-5-5 (I3): the client stops this batch, so the server must not keep its siblings
      // staged. No filename cancels every file still staging on this socket (a pull already
      // under way runs to its end), and closing the socket releases anything admitted later.
      if (socket.connected) {
        socket.emit(SocketEvents.CancelUpload);
      }
      resetUploadState(true);
    } else {
      // Reset upload state and clean up listeners
      resetUploadState();
    }

    setIsLoading(false);
  });

  socket.on("connect_error", err => {
    console.error("connect_error:", err.message, err);
    abandonUpload(
      `Could not connect to the upload service. ${err.message}`.trim()
    );
  });

  socket.on(SocketEvents.Disconnect, () => {
    console.log("Socket.IO disconnected");
    finishUpload();
  });

  const finishUpload = () => {
    setIsLoading(false);

    // Reset upload state and close socket
    resetUploadState(true);
  };

  // Return cancellation functions
  return {
    cancelAll: cancelAllUploads,
  };
};
